import { DefinedUseQueryResult, useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArchivedDocumentWithDetails, Claim } from "@/types";
import { Grid } from "@mantine/core";
import { getDocuments } from "@/services/Documents";
import { useAuth } from "@/contexts/AuthContext";
import Accordion from "@/components/organisms/AccordionDrawer";
import Text from "@/components/atoms/Text";
import Loader from "@/components/atoms/Loader";
import Group from "@/components/atoms/Group";
import Badge, { badgeColor } from "@/components/atoms/Badge";
import ImageCarousel from "@/components/molecules/ImageCarousel";

import styles from "./styles.module.css";
import ClaimStatusEnum from "@/constants/enums/ClaimStatusEnum";
import Space from "@/components/atoms/Space";
import dayjs from "dayjs";
interface ViewClaimProps {
  claim: DefinedUseQueryResult<Claim[] | undefined, Error>;
}

const ViewClaim = ({ claim }: ViewClaimProps) => {
  const { data: claims, isLoading } = claim;

  return (
    <div>
      {isLoading && <Loader />}
      {claims && claims.map((claim) => <ViewClaimAccordion key={claim.id} claim={claim} />)}
    </div>
  );
};

export default ViewClaim;

interface ViewClaimAccordionProps {
  claim: Claim;
}

const ViewClaimAccordion = ({ claim }: ViewClaimAccordionProps) => {
  const { t } = useTranslation();
  return (
    <Accordion title={<AccordionHeader claim={claim} />}>
      <Grid className={styles.grid}>
        <Grid.Col span={3}>
          <Text size="sm" color="secondary">
            {t("claims:problem")}
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Text size="sm">
            {claim.problemTypes.map((problem) => t("enumerables:problemTypes." + problem)).join(", ")}
          </Text>
        </Grid.Col>

        <Grid.Col span={3}>
          <Text size="sm" color="secondary">
            {t("claims:preferredSolution")}
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Text size="sm">
            {claim.preferredResolution ? t("enumerables:preferredSolutions." + claim.preferredResolution) : "-"}
          </Text>
        </Grid.Col>

        <Grid.Col span={3}>
          <Text size="sm" color="secondary">
            {t("claims:comment")}
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Text size="sm" color="tertiary">
            {claim.customerNote || "-"}
          </Text>
        </Grid.Col>

        <Grid.Col span={3}>
          <Text size="sm" color="secondary">
            {t("common:images")}
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <ClaimImageCarousel claim={claim} />
        </Grid.Col>
      </Grid>

      <ClaimSolutionRow claim={claim} />
    </Accordion>
  );
};

interface AccordionHeaderProps {
  claim: Claim;
}
const AccordionHeader = ({ claim }: AccordionHeaderProps) => {
  const { t } = useTranslation();

  let statusToBadgeColor = badgeColor.primary;
  if (claim.status === "RESOLVED") {
    statusToBadgeColor = badgeColor.success;
  } else if (claim.status === "REJECTED") {
    statusToBadgeColor = badgeColor.error;
  }

  return (
    <Group justify="space-between" grow>
      <Text color="secondary" size="sm">
        {dayjs(claim.created).format("DD.MM.YYYY")}
      </Text>
      <Badge radius="xs" size="lg" color={statusToBadgeColor} mr="15px">
        {t("enumerables:claimStatus." + claim.status)}
      </Badge>
    </Group>
  );
};

interface ImageCarouselProps {
  claim: Claim;
}

const ClaimImageCarousel = ({ claim }: ImageCarouselProps) => {
  const { currentCompany } = useAuth();

  const images = useQueries({
    queries: claim.attachments
      ? claim.attachments.map((attachment) => ({
          queryKey: ["getDocumentById", attachment],
          queryFn: () => getDocuments(currentCompany?.companyId as number, attachment),
          staleTime: 2 * 1000 * 60, //2min
        }))
      : [],
    combine: (results) => {
      return {
        images: results.map((result) => result.data?.data) as ArchivedDocumentWithDetails[],
        pending: results.some((result) => result.isPending),
      };
    },
  });

  return (
    <>
      {images.pending && <Loader />}
      {!images.pending && images && (
        <ImageCarousel images={images.images.map((doc) => doc)} imagesPrSlide={4} height={90} />
      )}
    </>
  );
};

const ClaimSolutionRow = ({ claim }: { claim: Claim }) => {
  const { t } = useTranslation();
  return (
    <>
      {(claim.status === ClaimStatusEnum.ACCEPTED ||
        claim.status === ClaimStatusEnum.REJECTED ||
        claim.status === ClaimStatusEnum.RESOLVED) && (
        <>
          <Space height="30px" />
          <Grid
            className={`${styles.grid}`}
            style={{
              backgroundColor:
                claim.status === ClaimStatusEnum.REJECTED
                  ? "var(--color-brand-grapefruit-10)"
                  : "var(--color-dark-green-10)",
            }}
          >
            <Grid.Col span={3}>
              <Text size="sm" color="secondary">
                {t("claims:solution")}
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <Text size="sm">
                {claim.proposals && claim.proposals.length > 0
                  ? t("enumerables:preferredSolutions." + claim.proposals)
                  : "-"}
              </Text>
            </Grid.Col>

            <Grid.Col span={3}>
              <Text size="sm" color="secondary">
                {t("claims:comment")}
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <Text size="sm">{claim.adminResolutionNote}</Text>
            </Grid.Col>
          </Grid>
        </>
      )}
    </>
  );
};
